import { useQuery } from '@tanstack/react-query';
// api
import { smartCityApi } from '@/domains/traffic-monitoring/apis/smart-city';
import type { SeriesItem } from '@/shared/components/ui/LineChart/LineChart';

// EVT_TP_* codes from GET /dashboard/anomaly/trend `by_type`, mapped to the
// four series the CCTV detail chart displays.
const TYPE_SERIES = [
  { code: 'EVT_TP_SPD', name: 'Overspeed', color: 'warning', startColor: 'warning' },
  { code: 'EVT_TP_JW', name: 'Jaywalk', color: 'danger', startColor: 'accentSoftRed' },
  { code: 'EVT_TP_WWD', name: 'Contra Flow', color: 'yellowPrimary', startColor: 'yellowPrimary' },
  { code: 'EVT_TP_STP', name: 'Sudden Stop', color: 'accentBlue', startColor: 'accentBlue' },
] as const;

const parseHours = (filter?: string): number => {
  const num = parseInt((filter ?? '1h').replace('h', ''), 10);
  return Number.isNaN(num) ? 1 : num;
};

// "2026-08-11T08:00:00+07:00" -> "08:00"
const isoTimeLabel = (iso?: string): string => (iso ? iso.slice(11, 16) : '');

// PSC_PC0201001_BE_005 drill-down: anomaly trend for one CCTV.
export const useAnomalyTrend = ({ filter = '1h', cctvId }: { filter?: string; cctvId?: number }) => {
  const hours = parseHours(filter);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['anomalyTrend', hours, cctvId],
    queryFn: () =>
      smartCityApi.getAnomalyTrend(cctvId != null ? { hours, cctv_id: cctvId } : { hours }),
    enabled: cctvId != null,
    // ES aggregation is heavier than the PG counters - keep the SmartCity
    // cadence (30s), not the 2s crossroad poll.
    refetchInterval: 30000,
    staleTime: 10000,
    placeholderData: (prev) => prev,
  });

  const buckets = data?.data?.buckets ?? [];
  const xAxisData = buckets.map((b) => isoTimeLabel(b.timestamp));
  const series: SeriesItem[] = TYPE_SERIES.map((t) => ({
    name: t.name,
    color: t.color,
    data: buckets.map((b) => b.by_type?.[t.code] ?? 0),
    areaStyle: { startColor: t.startColor, endColor: 'greyPrimary', opacity: 0.3 },
  }));

  return { xAxisData, series, isLoading, isError };
};
